// LearnCore Frontend - 编辑阅读材料页面

import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { apiClient } from '../api/client';
import Loading from '../components/Loading';
import type {
  UpdateReadingPassageRequest,
  ReadingPassageWithDetails
} from '../types';
import {
  DocumentTextIcon,
  SparklesIcon,
  EyeIcon,
  EyeSlashIcon,
  ArrowLeftIcon
} from '@heroicons/react/24/outline';

export default function EditPassagePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [passage, setPassage] = useState<ReadingPassageWithDetails | null>(null);

  // 基础信息
  const [title, setTitle] = useState('');
  const [originalText, setOriginalText] = useState('');
  const [translation, setTranslation] = useState('');
  const [isPublic, setIsPublic] = useState(true);

  // 子内容列表
  const [vocabularies, setVocabularies] = useState<Array<{
    id?: string;
    word: string;
    explanation: string;
    example_sentence?: string;
  }>>([]);

  const [grammarPoints, setGrammarPoints] = useState<Array<{
    id?: string;
    point_name: string;
    explanation: string;
    example_text?: string;
  }>>([]);

  const [collocations, setCollocations] = useState<Array<{
    id?: string;
    collocation: string;
    meaning: string;
    usage_note?: string;
  }>>([]);

  const [complexSentences, setComplexSentences] = useState<Array<{
    id?: string;
    sentence: string;
    structure_analysis: string;
    translation: string;
  }>>([]);

  // UI状态
  const [isExtracting, setIsExtracting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      loadPassage();
    }
  }, [id]);

  const loadPassage = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.getReadingPassage(id!);
      const passage = response.passage;

      // 检查权限
      if (!response.is_owner) {
        setError('无权编辑此阅读材料');
        return;
      }

      setPassage(passage);
      setTitle(passage.title);
      setOriginalText(passage.original_text);
      setTranslation(passage.translation || '');
      setIsPublic(passage.is_public === 1);
      setVocabularies(passage.vocabularies);
      setGrammarPoints(passage.grammar_points);
      setCollocations(passage.collocations);
      setComplexSentences(passage.complex_sentences);

    } catch (err: any) {
      setError(err.response?.data?.error || '加载阅读材料失败');
    } finally {
      setLoading(false);
    }
  };

  // 智能提取功能
  const handleExtract = async () => {
    if (!originalText.trim()) {
      setError('请输入原文内容');
      return;
    }

    try {
      setIsExtracting(true);
      setError(null);

      // 智能提取功能暂时禁用，等待后端实现
      alert('智能提取功能暂时不可用');

    } catch (err: any) {
      setError(err.response?.data?.error || '智能提取失败，请稍后重试');
    } finally {
      setIsExtracting(false);
    }
  };

  // 保存阅读材料
  const handleSave = async () => {
    if (!title.trim()) {
      setError('请输入标题');
      return;
    }

    if (!originalText.trim()) {
      setError('请输入原文');
      return;
    }

    try {
      setIsSaving(true);
      setError(null);

      const updateData: UpdateReadingPassageRequest = {
        title: title.trim(),
        original_text: originalText.trim(),
        translation: translation.trim() || undefined,
        is_public: isPublic,
        vocabularies: vocabularies.length > 0 ? vocabularies : undefined,
        grammar_points: grammarPoints.length > 0 ? grammarPoints : undefined,
        collocations: collocations.length > 0 ? collocations : undefined,
        complex_sentences: complexSentences.length > 0 ? complexSentences : undefined,
      };

      await apiClient.updateReadingPassage(id!, updateData);
      navigate(`/passages/${id}/study`);

    } catch (err: any) {
      setError(err.response?.data?.error || '保存失败，请稍后重试');
    } finally {
      setIsSaving(false);
    }
  };

  // 编辑功能暂时禁用，等待完整实现

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Loading size="lg" text="加载阅读材料..." />
      </div>
    );
  }

  if (error && !passage) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">加载失败</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <Link to="/passages" className="btn-primary">
            返回阅读材料列表
          </Link>
        </div>
      </div>
    );
  }

  // 使用与CreatePassagePage相同的UI
  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* 页面头部 */}
      <div className="mb-8">
        <div className="flex items-center space-x-4 mb-4">
          <Link
            to={`/passages/${id}/study`}
            className="flex items-center text-gray-600 hover:text-gray-900"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            返回
          </Link>
        </div>
        
        <h1 className="text-3xl font-bold text-gray-900 flex items-center">
          <DocumentTextIcon className="h-8 w-8 mr-3 text-primary-600" />
          编辑阅读材料
        </h1>
        <p className="mt-2 text-gray-600">
          编辑英文原文和学习内容
        </p>
      </div>

      {/* 错误提示 */}
      {error && passage && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">操作失败</h3>
              <div className="mt-2 text-sm text-red-700">{error}</div>
              <div className="mt-4">
                <button
                  onClick={() => setError(null)}
                  className="btn-outline text-sm"
                >
                  关闭
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* 左侧表单区域 - 与CreatePassagePage相同的UI结构，此处省略重复代码 */}
        {/* 实际实现时需要复制CreatePassagePage中的完整表单UI */}
        <div className="lg:col-span-2 space-y-6">
          {/* 基本信息 */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">基本信息</h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  标题 *
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="请输入阅读材料的标题"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  英文原文 *
                </label>
                <textarea
                  value={originalText}
                  onChange={(e) => setOriginalText(e.target.value)}
                  rows={8}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="请输入英文原文段落..."
                />
              </div>

              <div className="flex items-center justify-between">
                <button
                  onClick={() => setIsPublic(!isPublic)}
                  className={`flex items-center px-3 py-2 rounded-md text-sm font-medium ${
                    isPublic
                      ? 'bg-green-100 text-green-800 hover:bg-green-200'
                      : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                  }`}
                >
                  {isPublic ? (
                    <>
                      <EyeIcon className="h-4 w-4 mr-2" />
                      公开
                    </>
                  ) : (
                    <>
                      <EyeSlashIcon className="h-4 w-4 mr-2" />
                      私有
                    </>
                  )}
                </button>

                <button
                  onClick={handleExtract}
                  disabled={isExtracting || !originalText.trim()}
                  className="btn-primary flex items-center"
                >
                  {isExtracting ? (
                    <>
                      <div className="h-4 w-4 mr-2 animate-spin rounded-full border-2 border-white border-t-transparent" />
                      提取中...
                    </>
                  ) : (
                    <>
                      <SparklesIcon className="h-4 w-4 mr-2" />
                      智能提取
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* 译文编辑 - 使用与CreatePassagePage相同的标签页UI */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                中文译文
              </label>
              <textarea
                value={translation}
                onChange={(e) => setTranslation(e.target.value)}
                rows={6}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                placeholder="请输入或编辑中文译文..."
              />
            </div>
          </div>
        </div>

        {/* 右侧操作面板 */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg border border-gray-200 p-6 sticky top-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">操作面板</h3>

            <div className="space-y-4">
              <div className="text-sm text-gray-600">
                <p className="mb-2">✏️ <strong>编辑提示：</strong></p>
                <ol className="list-decimal list-inside space-y-1 text-xs">
                  <li>修改标题和英文原文</li>
                  <li>可重新使用"智能提取"</li>
                  <li>检查和编辑提取的内容</li>
                  <li>点击保存完成更新</li>
                </ol>
              </div>

              <div className="border-t pt-4">
                <button
                  onClick={handleSave}
                  disabled={isSaving || !title.trim() || !originalText.trim()}
                  className="w-full btn-primary py-3 disabled:opacity-50"
                >
                  {isSaving ? (
                    <>
                      <div className="h-4 w-4 mr-2 animate-spin rounded-full border-2 border-white border-t-transparent inline-block" />
                      保存中...
                    </>
                  ) : (
                    '保存更改'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
